import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  UserCheck, 
  UserX, 
  Calendar, 
  CreditCard, 
  FileText, 
  AlertTriangle, 
  Settings, 
  BarChart3, 
  Search, 
  Filter, 
  MoreVertical, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Shield, 
  ShieldOff,
  Clock,
  TrendingUp,
  Activity,
  MessageSquare,
  DollarSign,
  BookOpen,
  UserPlus,
  UserMinus,
  RefreshCw,
  Download,
  Upload,
  Trash2,
  Edit,
  Phone,
  Mail,
  MapPin,
  Star,
  CalendarDays,
  Clock as ClockIcon,
  AlertCircle,
  CheckSquare,
  Square,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  Filter as FilterIcon,
  X,
  Plus,
  Minus,
  Zap,
  Target,
  Award,
  TrendingDown,
  Users as UsersIcon,
  Briefcase,
  Home,
  LogOut
} from 'lucide-react';
import toast from 'react-hot-toast';
import { db } from '../config/firebase';
import { collection, doc, getDoc, getDocs, addDoc, updateDoc, query, where, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { userService, lawyerService } from '../services/firebaseService';
import { UsersTab, LawyersTab } from './AdminTabComponents';
import { BookingsTab, PaymentsTab, CasesTab, ReportsTab, SettingsTab } from './AdminRemainingTabs';

const WebOnlyAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalLawyers: 0,
    activeLawyers: 0,
    pendingLawyers: 0,
    activeBookings: 0,
    ongoingCases: 0,
    totalRevenue: 0
  });
  const [users, setUsers] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [cases, setCases] = useState([]);
  const [payments, setPayments] = useState([]);
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [realtimeSubscriptions, setRealtimeSubscriptions] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  // Initialize dashboard data
  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        setIsLoading(true);
        
        // Load initial data
        await loadInitialData();
        
        // Set up real-time subscriptions
        setupRealtimeSubscriptions();
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error initializing dashboard:', error);
        toast.error('Failed to initialize dashboard');
        setIsLoading(false);
      }
    };

    initializeDashboard();

    return () => {
      // Cleanup real-time subscriptions
      realtimeSubscriptions.forEach(sub => {
        if (sub && typeof sub.unsubscribe === 'function') {
          sub.unsubscribe();
        }
      });
    };
  }, []);

  const setupRealtimeSubscriptions = () => {
    const subscriptions = [];

    // For now, we'll use polling instead of real-time subscriptions
    // Firebase real-time subscriptions can be added later if needed
    const pollInterval = setInterval(() => {
        loadUsers();
        loadLawyers();
        loadBookings();
    }, 30000); // Poll every 30 seconds

    subscriptions.push(pollInterval);
    setRealtimeSubscriptions(subscriptions);
  };

  const loadInitialData = async () => {
    try {
      // Load data in sequence to ensure lawyers are loaded before stats
      await loadUsers();
      await loadLawyers();
      await loadBookings();
      await loadCases();
      await loadPayments();
      await loadReports();
      await loadStats(); // This now depends on lawyers being loaded
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load data');
    }
  };

  const loadUsers = async () => {
    try {
      console.log('Loading users...');
      // Only load clients (users who chose "Find Lawyer" in role selection)
      const usersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'client')
      );
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure we have fallback values
          name: data.name || data.full_name || data.fullName || data.displayName || 'Unknown User',
          email: data.email || 'No email',
          phone: data.phone || data.mobileNumber || data.phoneNumber || 'No phone',
          mobileNumber: data.mobileNumber || data.phone || data.phoneNumber || 'No phone',
          full_name: data.full_name || data.fullName || data.name || 'Unknown User',
          aadhar_number: data.aadhar_number || data.aadharNumber || 'N/A',
          date_of_birth: data.date_of_birth || data.dateOfBirth || 'N/A',
          city: data.city || data.custom_city || 'N/A',
          bio: data.bio || 'N/A',
          profile_picture: data.profile_picture || data.profilePicture || null,
          profile_complete: data.profile_complete || false,
          isActive: data.isActive !== false, // Default to true
          createdAt: data.createdAt || data.created_at || new Date().toISOString()
        };
      });
      console.log('Loaded clients:', usersData);
      console.log('Clients count:', usersData.length);
      setUsers(usersData || []);
    } catch (error) {
      console.error('Error loading users:', error);
      // Set empty array on error to prevent undefined issues
      setUsers([]);
      toast.error('Failed to load users');
    }
  };

  const loadLawyers = async () => {
    try {
      console.log('Loading lawyers...');
      // Try simple query first without ordering
      const lawyersQuery = collection(db, 'lawyers');
      const lawyersSnapshot = await getDocs(lawyersQuery);
      const lawyersData = lawyersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure we have fallback values for display
          fullName: data.fullName || data.full_name || data.name || data.user_profiles?.full_name || 'Unknown Lawyer',
          email: data.email || data.user_profiles?.email || 'No email',
          phone: data.phone || data.mobileNumber || data.user_profiles?.phone || 'No phone',
          specialization: data.specialization || data.specializations?.[0] || 'General Law',
          experience_years: data.experience_years || data.experience || 0,
          verificationStatus: data.verificationStatus || data.status || 'pending',
          profilePicture: data.profilePicture || data.profile_picture || data.avatar || null,
          createdAt: data.createdAt || data.created_at || new Date().toISOString()
        };
      });
      console.log('Loaded lawyers:', lawyersData);
      console.log('Lawyers count:', lawyersData.length);
      setLawyers(lawyersData || []);
    } catch (error) {
      console.error('Error loading lawyers:', error);
      // Set empty array on error to prevent undefined issues
      setLawyers([]);
      toast.error('Failed to load lawyers');
    }
  };

  const loadBookings = async () => {
    try {
      console.log('Loading bookings...');
      // Get appointments from Firebase
      const bookingsQuery = collection(db, 'appointments');
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookingsData = bookingsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure we have proper field mapping
          clientName: data.clientName || data.client_name || data.userName || 'Unknown Client',
          lawyerName: data.lawyerName || data.lawyer_name || data.advocateName || 'Unknown Lawyer',
          clientEmail: data.clientEmail || data.client_email || data.userEmail || 'No email',
          lawyerEmail: data.lawyerEmail || data.lawyer_email || data.advocateEmail || 'No email',
          appointmentDate: data.appointmentDate || data.appointment_date || data.date || new Date().toISOString(),
          appointmentTime: data.appointmentTime || data.appointment_time || data.time || '00:00',
          consultationType: data.consultationType || data.consultation_type || 'text',
          status: data.status || 'pending',
          amount: data.amount || data.fee || data.consultation_fee || 0,
          description: data.description || data.case_description || 'No description',
          createdAt: data.createdAt || data.created_at || new Date().toISOString(),
          updatedAt: data.updatedAt || data.updated_at || new Date().toISOString()
        };
      });
      console.log('Loaded bookings:', bookingsData);
      console.log('Bookings count:', bookingsData.length);
      setBookings(bookingsData || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
      // Set empty array on error to prevent undefined issues
      setBookings([]);
      toast.error('Failed to load bookings');
    }
  };

  const loadCases = async () => {
    try {
      console.log('Loading cases...');
      // Get cases from Firebase - using appointments as cases for now
      const casesQuery = collection(db, 'appointments');
      const casesSnapshot = await getDocs(casesQuery);
      const casesData = casesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure we have proper field mapping
          caseTitle: data.caseTitle || data.title || `Consultation with ${data.lawyerName || 'Lawyer'}`,
          clientName: data.clientName || data.userName || data.user_name || 'Unknown Client',
          lawyerName: data.lawyerName || data.lawyer_name || data.advocateName || 'Unknown Lawyer',
          caseType: data.caseType || data.type || data.consultationType || 'Consultation',
          status: data.status || 'pending',
          priority: data.priority || data.isEmergency ? 'high' : 'medium',
          description: data.description || data.caseDescription || `Consultation scheduled for ${data.date} at ${data.time}`,
          amount: data.totalAmount || data.amount || data.price || 0,
          consultationTypes: data.selectedConsultationTypes || data.types || [],
          fileReadingCharges: data.fileReadingCharges || data.file_reading_charges || 0,
          isEmergency: data.isEmergency || false,
          createdAt: data.createdAt || data.created_at || new Date().toISOString(),
          updatedAt: data.updatedAt || data.updated_at || new Date().toISOString()
        };
      });
      console.log('Loaded cases:', casesData);
      setCases(casesData || []);
    } catch (error) {
      console.error('Error loading cases:', error);
      setCases([]);
    }
  };

  const loadPayments = async () => {
    try {
      console.log('Loading payments...');
      // Get payments from Firebase
      const paymentsQuery = collection(db, 'payments');
      const paymentsSnapshot = await getDocs(paymentsQuery);
      const paymentsData = paymentsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure we have proper field mapping
          userName: data.userName || data.user_name || data.clientName || 'Unknown User',
          userEmail: data.userEmail || data.user_email || data.clientEmail || 'No email',
          lawyerName: data.lawyerName || data.lawyer_name || data.advocateName || 'Unknown Lawyer',
          lawyerEmail: data.lawyerEmail || data.lawyer_email || data.advocateEmail || 'No email',
          amount: data.amount || data.total_amount || data.fee || 0,
          status: data.status || 'pending',
          paymentMethod: data.paymentMethod || data.payment_method || 'online',
          transactionId: data.transactionId || data.transaction_id || data.razorpay_payment_id || 'N/A',
          bookingId: data.bookingId || data.booking_id || data.appointment_id || 'N/A',
          description: data.description || data.notes || 'Payment for consultation',
          createdAt: data.createdAt || data.created_at || new Date().toISOString(),
          updatedAt: data.updatedAt || data.updated_at || new Date().toISOString()
        };
      });
      console.log('Loaded payments:', paymentsData);
      console.log('Payments count:', paymentsData.length);
      setPayments(paymentsData || []);
    } catch (error) {
      console.error('Error loading payments:', error);
      // Set empty array on error to prevent undefined issues  
      setPayments([]);
      // Don't show error toast as payments collection might not exist yet
      console.log('Payments collection might not exist yet, this is normal for new installations');
    }
  };

  const loadReports = async () => {
    try {
      console.log('Loading reports and disputes...');
      // Get reports from Firebase - using a reports collection
      const reportsQuery = collection(db, 'reports');
      const reportsSnapshot = await getDocs(reportsQuery);
      const reportsData = reportsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure we have proper field mapping
          reportTitle: data.reportTitle || data.title || data.subject || 'Untitled Report',
          reporterName: data.reporterName || data.reporter_name || data.userName || 'Anonymous',
          reporterEmail: data.reporterEmail || data.reporter_email || data.userEmail || 'No email',
          reportedUser: data.reportedUser || data.reported_user || data.lawyerName || 'Unknown',
          reportType: data.reportType || data.type || data.category || 'General',
          status: data.status || 'pending',
          priority: data.priority || 'medium',
          description: data.description || data.details || data.message || 'No description provided',
          evidence: data.evidence || data.attachments || [],
          resolution: data.resolution || data.adminNotes || '',
          createdAt: data.createdAt || data.created_at || new Date().toISOString(),
          updatedAt: data.updatedAt || data.updated_at || new Date().toISOString()
        };
      });
      console.log('Loaded reports:', reportsData);
      setReports(reportsData || []);
    } catch (error) {
      console.error('Error loading reports:', error);
      // Create some mock reports for demonstration
      const mockReports = [
        {
          id: '1',
          reportTitle: 'Payment Dispute',
          reporterName: 'John Doe',
          reporterEmail: 'john@example.com',
          reportedUser: 'Adv. Ananya Sharma',
          reportType: 'Payment Issue',
          status: 'pending',
          priority: 'high',
          description: 'Payment was deducted but consultation was not confirmed',
          evidence: [],
          resolution: '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: '2',
          reportTitle: 'Professional Conduct',
          reporterName: 'Jane Smith',
          reporterEmail: 'jane@example.com',
          reportedUser: 'Adv. Rajesh Kumar',
          reportType: 'Professional Misconduct',
          status: 'investigating',
          priority: 'medium',
          description: 'Lawyer was not available during scheduled consultation time',
          evidence: [],
          resolution: 'Under investigation',
          createdAt: new Date(Date.now() - 86400000).toISOString(),
          updatedAt: new Date().toISOString()
        }
      ];
      setReports(mockReports);
    }
  };

  const loadStats = async () => {
    try {
      // Get client stats (only users who chose "Find Lawyer" in role selection)
      const usersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'client')
      );
      const usersSnapshot = await getDocs(usersQuery);
      const totalUsers = usersSnapshot.size;

      // Generate comprehensive recent activities
      const activities = [];
      
      // Add lawyer profile activities
      lawyers.forEach(lawyer => {
        activities.push({
          id: `lawyer-${lawyer.id}`,
          type: 'lawyer_profile',
          title: `${lawyer.fullName || lawyer.full_name || 'Unknown Lawyer'} profile`,
          status: lawyer.verificationStatus || 'pending',
          timestamp: lawyer.createdAt || new Date().toISOString(),
          description: `Lawyer profile ${lawyer.verificationStatus === 'pending' ? 'submitted for review' : lawyer.verificationStatus}`,
          icon: 'UserCheck',
          color: lawyer.verificationStatus === 'approved' ? 'green' : lawyer.verificationStatus === 'rejected' ? 'red' : 'yellow'
        });
      });
      
      // Add user registration activities
      users.forEach(user => {
        activities.push({
          id: `user-${user.id}`,
          type: 'user_registration',
          title: `${user.full_name || user.name || user.fullName || 'New User'} registered`,
          status: user.isActive !== false ? 'active' : 'inactive',
          timestamp: user.createdAt || new Date().toISOString(),
          description: `New client account created - ${user.email || 'No email'}`,
          icon: 'UserPlus',
          color: user.isActive !== false ? 'green' : 'red'
        });
      });
      
      // Add booking activities
      bookings.forEach(booking => {
        activities.push({
          id: `booking-${booking.id}`,
          type: 'booking',
          title: `New booking created`,
          status: booking.status || 'pending',
          timestamp: booking.createdAt || booking.created_at || new Date().toISOString(),
          description: `Booking ${booking.status || 'pending'} - ${booking.clientName || 'Client'} with ${booking.lawyerName || 'Lawyer'}`,
          icon: 'Calendar',
          color: booking.status === 'confirmed' ? 'green' : booking.status === 'cancelled' ? 'red' : 'yellow'
        });
      });
      
      // Sort by timestamp and take top 10
      const sortedActivities = activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 10);
      
      setRecentActivities(activities);

      // Get lawyer stats
      const lawyersQuery = collection(db, 'lawyers');
      const lawyersSnapshot = await getDocs(lawyersQuery);
      const totalLawyers = lawyersSnapshot.size;

      // Count approved and pending lawyers manually
      let activeLawyers = 0;
      let pendingLawyers = 0;
      lawyersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const status = data.verificationStatus || data.status || 'pending';
        if (status === 'approved') {
          activeLawyers++;
        } else if (status === 'pending') {
          pendingLawyers++;
        }
      });

      // Get appointment stats
      const appointmentsQuery = collection(db, 'appointments');
      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      
      let activeBookings = 0;
      let ongoingCases = 0;
      appointmentsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.status === 'pending' || data.status === 'confirmed') {
          activeBookings++;
        }
        if (data.status === 'confirmed' || data.status === 'in_progress') {
          ongoingCases++;
        }
      });

      console.log('Stats loaded:', {
        totalUsers,
        totalLawyers,
        activeLawyers,
        pendingLawyers,
        activeBookings,
        ongoingCases
      });
      
      console.log('Lawyers data sample:', lawyersSnapshot.docs.slice(0, 3).map(doc => ({
        id: doc.id,
        verificationStatus: doc.data().verificationStatus,
        status: doc.data().status,
        fullName: doc.data().fullName || doc.data().full_name
      })));

      setStats({
        totalUsers: totalUsers || 0,
        totalLawyers: totalLawyers || 0,
        activeLawyers: activeLawyers || 0,
        pendingLawyers: pendingLawyers || 0,
        activeBookings: activeBookings || 0,
        ongoingCases: ongoingCases || 0,
        totalRevenue: 0 // Will be calculated from payments
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      console.log('Refreshing admin dashboard data...');
      await Promise.all([
        loadUsers(),
        loadLawyers(),
        loadBookings(),
        loadCases(),
        loadPayments(),
        loadReports(),
        loadStats()
      ]);
      toast.success('Data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleVerifyLawyer = async (lawyerId, verified = true) => {
    try {
      const status = verified ? 'approved' : 'rejected';
      
      // Update lawyer status directly in Firebase
      const lawyerRef = doc(db, 'lawyers', lawyerId);
      await updateDoc(lawyerRef, {
        verificationStatus: status,
        isVerified: verified, // Set the isVerified flag
        profileComplete: true, // Ensure profile is marked as complete
        verificationDate: new Date().toISOString(),
        verifiedBy: 'admin', // You can replace with actual admin ID
        updated_at: new Date().toISOString()
      });
      
        // Send email notification
        await sendLawyerVerificationEmail(lawyerId, verified);
        
        toast.success(`Lawyer ${verified ? 'verified' : 'rejected'} successfully`);
        
        // Refresh data
        await loadLawyers();
        await loadStats();
    } catch (error) {
      console.error('Error verifying lawyer:', error);
      toast.error('Failed to verify lawyer');
    }
  };

  const sendLawyerVerificationEmail = async (lawyerId, approved) => {
    try {
      // Find the lawyer in the current lawyers array
      const lawyer = lawyers.find(l => l.id === lawyerId);
      if (!lawyer) {
        console.error('Lawyer not found:', lawyerId);
        return;
      }
      
      const lawyerName = lawyer.fullName || lawyer.full_name || lawyer.user_profiles?.full_name || 'Lawyer';
      const lawyerEmail = lawyer.email || lawyer.user_profiles?.email;
      
      if (!lawyerEmail) {
        console.error('Lawyer email not found:', lawyerId);
        return;
      }
      
      // Send email using Supabase Edge Functions or external service
      // For now, we'll log the email details
      console.log('Sending verification email:', {
        to: lawyerEmail,
        lawyerName: lawyerName,
        status: approved ? 'approved' : 'rejected',
        message: approved 
          ? 'Your lawyer profile has been approved. You can now start accepting clients.'
          : 'Your lawyer profile has been rejected. Please update your information and resubmit.'
      });

      // TODO: Integrate with email service (SendGrid, AWS SES, etc.)
      // For now, we'll use a mock email service
      await mockSendEmail({
        to: lawyerEmail,
        subject: `Lawyer Profile ${approved ? 'Approved' : 'Rejected'}`,
        body: approved 
          ? `Dear ${lawyerName},\n\nCongratulations! Your lawyer profile has been approved. You can now start accepting clients and providing legal services.\n\nBest regards,\nNyaySahayak Team`
          : `Dear ${lawyerName},\n\nWe regret to inform you that your lawyer profile has been rejected. Please review and update your information, then resubmit your profile.\n\nBest regards,\nNyaySahayak Team`
      });

    } catch (error) {
      console.error('Error sending verification email:', error);
    }
  };

  const mockSendEmail = async (emailData) => {
    // Mock email sending - replace with actual email service
    console.log('Mock email sent:', emailData);
    return Promise.resolve({ success: true });
  };

  const handleBulkAction = async (action, items) => {
    try {
      switch (action) {
        case 'verify':
          await Promise.all(items.map(id => handleVerifyLawyer(id, true)));
          break;
        case 'reject':
          await Promise.all(items.map(id => handleVerifyLawyer(id, false)));
          break;
        case 'delete':
          // Implement bulk delete
          break;
        default:
          break;
      }
      
      setSelectedItems([]);
      toast.success(`Bulk ${action} completed successfully`);
    } catch (error) {
      console.error('Error performing bulk action:', error);
      toast.error('Failed to perform bulk action');
    }
  };

  const handleLogout = async () => {
    try {
      // Clear any admin session data
      localStorage.removeItem('adminSession');
      sessionStorage.removeItem('adminSession');
      
      // Redirect to admin login page
      window.location.href = '/admin-login';
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to logout');
    }
  };

  const filteredLawyers = lawyers.filter(lawyer => {
    const matchesSearch = lawyer.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lawyer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lawyer.specialization?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || lawyer.verificationStatus === filterType;
    
    return matchesSearch && matchesFilter;
  });

  const sortedLawyers = [...filteredLawyers].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = a.fullName || '';
        bValue = b.fullName || '';
        break;
      case 'email':
        aValue = a.email || '';
        bValue = b.email || '';
        break;
      case 'status':
        aValue = a.verificationStatus || '';
        bValue = b.verificationStatus || '';
        break;
      case 'createdAt':
      default:
        aValue = new Date(a.createdAt || 0);
        bValue = new Date(b.createdAt || 0);
        break;
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Filter and sort users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || 
                         (filterType === 'active' && user.isActive !== false) ||
                         (filterType === 'inactive' && user.isActive === false);
    
    return matchesSearch && matchesFilter;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = a.name || '';
        bValue = b.name || '';
        break;
      case 'email':
        aValue = a.email || '';
        bValue = b.email || '';
        break;
      case 'lastActive':
        aValue = new Date(a.lastActive || 0);
        bValue = new Date(b.lastActive || 0);
        break;
      case 'createdAt':
      default:
        aValue = new Date(a.createdAt || 0);
        bValue = new Date(b.createdAt || 0);
        break;
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: Home },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'lawyers', label: 'Lawyers', icon: UserCheck },
              { id: 'bookings', label: 'Bookings', icon: Calendar },
              { id: 'payments', label: 'Payments', icon: CreditCard },
              { id: 'cases', label: 'Cases', icon: FileText },
              { id: 'reports', label: 'Reports', icon: BarChart3 },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Stats Grid */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  <span>{isRefreshing ? 'Refreshing...' : 'Refresh Data'}</span>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Users</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <UserCheck className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Active Lawyers</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.activeLawyers}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Clock className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Pending Lawyers</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.pendingLawyers}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Calendar className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Active Bookings</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.activeBookings}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {recentActivities.slice(0, 5).map((activity) => {
                      const getIcon = (iconName) => {
                        switch (iconName) {
                          case 'UserCheck': return <UserCheck className="w-4 h-4" />;
                          case 'UserPlus': return <UserPlus className="w-4 h-4" />;
                          case 'Calendar': return <Calendar className="w-4 h-4" />;
                          default: return <UserCheck className="w-4 h-4" />;
                        }
                      };
                      
                      const getIconColor = (color) => {
                        switch (color) {
                          case 'green': return 'text-green-600';
                          case 'red': return 'text-red-600';
                          case 'yellow': return 'text-yellow-600';
                          default: return 'text-gray-600';
                        }
                      };
                      
                      const getStatusColor = (status) => {
                        if (status === 'approved' || status === 'active' || status === 'confirmed') {
                          return 'bg-green-100 text-green-800';
                        } else if (status === 'rejected' || status === 'inactive' || status === 'cancelled') {
                          return 'bg-red-100 text-red-800';
                        } else {
                          return 'bg-yellow-100 text-yellow-800';
                        }
                      };
                      
                      return (
                        <div key={activity.id} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center ${getIconColor(activity.color)}`}>
                              {getIcon(activity.icon)}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {activity.title}
                              </p>
                              <p className="text-xs text-gray-500">
                                {activity.description}
                              </p>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(activity.status)}`}>
                            {activity.status}
                          </span>
                        </div>
                      );
                    })}
                    {recentActivities.length === 0 && (
                      <div className="text-center py-8">
                        <UserCheck className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No recent activity</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          No activities have been recorded yet.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'users' && (
            <motion.div
              key="users"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <UsersTab 
                users={sortedUsers}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filterType={filterType}
                setFilterType={setFilterType}
                sortBy={sortBy}
                sortOrder={sortOrder}
                toggleSort={(field) => {
                  if (sortBy === field) {
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortBy(field);
                    setSortOrder('desc');
                  }
                }}
                selectedItems={selectedItems}
                toggleItemSelection={(user) => {
                  setSelectedItems(prev => {
                    const exists = prev.find(item => item.id === user.id);
                    if (exists) {
                      return prev.filter(item => item.id !== user.id);
                    } else {
                      return [...prev, user];
                    }
                  });
                }}
                selectAll={() => setSelectedItems(users.map(user => user))}
                clearSelection={() => setSelectedItems([])}
                handleEnableDisableAccount={(userId, enable, type) => {
                  console.log(`${enable ? 'Enable' : 'Disable'} ${type} account:`, userId);
                  toast.success(`${enable ? 'Enabled' : 'Disabled'} account successfully`);
                }}
                handleBulkAction={(action) => {
                  console.log('Bulk action:', action, selectedItems);
                  toast.success(`Bulk action ${action} completed`);
                }}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
              />
            </motion.div>
          )}

          {activeTab === 'lawyers' && (
            <motion.div
              key="lawyers"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <LawyersTab 
                lawyers={sortedLawyers}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filterType={filterType}
                setFilterType={setFilterType}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                onVerifyLawyer={handleVerifyLawyer}
                onBulkAction={handleBulkAction}
                onRefresh={loadLawyers}
              />
            </motion.div>
          )}

          {activeTab === 'bookings' && (
            <motion.div
              key="bookings"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <BookingsTab 
                bookings={bookings}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filterType={filterType}
                setFilterType={setFilterType}
                sortBy={sortBy}
                sortOrder={sortOrder}
                toggleSort={(field) => {
                  if (sortBy === field) {
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortBy(field);
                    setSortOrder('desc');
                  }
                }}
                selectedItems={selectedItems}
                toggleItemSelection={(booking) => {
                  setSelectedItems(prev => {
                    const exists = prev.find(item => item.id === booking.id);
                    if (exists) {
                      return prev.filter(item => item.id !== booking.id);
                    } else {
                      return [...prev, booking];
                    }
                  });
                }}
                selectAll={() => setSelectedItems(bookings.map(booking => booking))}
                clearSelection={() => setSelectedItems([])}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                onRefresh={loadBookings}
              />
            </motion.div>
          )}

          {activeTab === 'payments' && (
            <motion.div
              key="payments"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <PaymentsTab 
                payments={payments}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filterType={filterType}
                setFilterType={setFilterType}
                sortBy={sortBy}
                sortOrder={sortOrder}
                toggleSort={(field) => {
                  if (sortBy === field) {
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortBy(field);
                    setSortOrder('desc');
                  }
                }}
                selectedItems={selectedItems}
                toggleItemSelection={(payment) => {
                  setSelectedItems(prev => {
                    const exists = prev.find(item => item.id === payment.id);
                    if (exists) {
                      return prev.filter(item => item.id !== payment.id);
                    } else {
                      return [...prev, payment];
                    }
                  });
                }}
                selectAll={() => setSelectedItems(payments.map(payment => payment))}
                clearSelection={() => setSelectedItems([])}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                onRefresh={loadPayments}
              />
            </motion.div>
          )}

          {activeTab === 'cases' && (
            <motion.div
              key="cases"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <CasesTab 
                cases={cases}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filterType={filterType}
                setFilterType={setFilterType}
                sortBy={sortBy}
                sortOrder={sortOrder}
                toggleSort={(field) => {
                  if (sortBy === field) {
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortBy(field);
                    setSortOrder('desc');
                  }
                }}
                selectedItems={selectedItems}
                toggleItemSelection={(caseItem) => {
                  setSelectedItems(prev => {
                    const exists = prev.find(item => item.id === caseItem.id);
                    if (exists) {
                      return prev.filter(item => item.id !== caseItem.id);
                    } else {
                      return [...prev, caseItem];
                    }
                  });
                }}
                selectAll={() => setSelectedItems(cases)}
                clearSelection={() => setSelectedItems([])}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                onRefresh={loadCases}
              />
            </motion.div>
          )}

          {activeTab === 'reports' && (
            <motion.div
              key="reports"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <ReportsTab 
                reports={reports}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filterType={filterType}
                setFilterType={setFilterType}
                sortBy={sortBy}
                sortOrder={sortOrder}
                toggleSort={(field) => {
                  if (sortBy === field) {
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortBy(field);
                    setSortOrder('desc');
                  }
                }}
                selectedItems={selectedItems}
                toggleItemSelection={(report) => {
                  setSelectedItems(prev => {
                    const exists = prev.find(item => item.id === report.id);
                    if (exists) {
                      return prev.filter(item => item.id !== report.id);
                    } else {
                      return [...prev, report];
                    }
                  });
                }}
                selectAll={() => setSelectedItems(reports)}
                clearSelection={() => setSelectedItems([])}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                onRefresh={loadReports}
              />
            </motion.div>
          )}

          {activeTab === 'settings' && (
            <motion.div
              key="settings"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <SettingsTab />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default WebOnlyAdminDashboard;
